import { parseQueryString } from '@fv/client-core'
import { fvDate, fvDateUtc } from '@fv/dates'
import type { CalendarLookupDTO } from '@fv/models'

import type {
  CalendarBucketStatus,
  CalendarDateKey,
  CalendarShipmentSummary,
  DayPanelTab,
  ShipmentDayEntry,
} from './types'

export function toCalendarDateKey(date: Date | string): CalendarDateKey {
  return fvDateUtc(date).local().format('YYYY-MM-DD')
}
export const parseCalendarDateKey = (dateKey: CalendarDateKey): Date => {
  return fvDate(dateKey, 'YYYY-MM-DD').toDate()
}

export type CalendarCell = {
  date: Date
  dayOfMonth: number
  isCurrentMonth: boolean
}

export function buildWeekLookupDTO(
  qs: string,
  weekStart: Date,
  weekEnd: Date,
): CalendarLookupDTO {
  const { sortBy, sortDirection, ...rest } = parseQueryString(qs)

  const dateRange = `${fvDateUtc(weekStart).startOf('day').valueOf()}-${fvDateUtc(weekEnd).endOf('day').valueOf()}`
  return {
    ...rest,
    dateRange,
  } as CalendarLookupDTO
}

const pickupEntry = (shipment: CalendarShipmentSummary): ShipmentDayEntry => {
  let status: CalendarBucketStatus = 'in-transit'

  if (shipment.trackingStatus === 'pending') {
    status = 'scheduled'
  }

  return {
    dateKey: toCalendarDateKey(new Date(shipment.pickupDate)),
    status,
  }
}

const deliveryEntry = (
  shipment: CalendarShipmentSummary,
): ShipmentDayEntry | undefined => {
  if (shipment.trackingStatus === 'out-for-delivery') {
    return {
      dateKey: toCalendarDateKey(
        new Date(shipment.deliveryDate ?? shipment.pickupDate),
      ),
      status: 'out-for-delivery',
    }
  }
  if (shipment.trackingStatus !== 'delivered') {
    return undefined
  }
  if (!shipment.deliveryDate) {
    return undefined
  }
  return {
    dateKey: toCalendarDateKey(new Date(shipment.deliveryDate)),
    status: 'delivered',
  }
}

export function getShipmentEntries(
  shipment: CalendarShipmentSummary,
): ShipmentDayEntry[] {
  const deliverEntry = deliveryEntry(shipment)
  return [pickupEntry(shipment), ...(deliverEntry ? [deliverEntry] : [])]
}

export function mapStatusToDayPanelTab(
  status: CalendarBucketStatus,
): DayPanelTab {
  if (status === 'out-for-delivery') {
    return 'delivered'
  }
  return status
}
